import { SkillTargetsModalComponent } from './../components/modals/skill-targets-modal/skill-targets-modal.component';
import { Injectable } from '@angular/core';
import { SimpleModalComponent } from '@app/shared/components/modal/simple-modal/simple-modal.component';
import { ModalService } from '@app/shared/services/modal.service';
import { AddCreateSegmentsToSkillStandardsModalComponent } from '@app/skill-standards/components/modals/add-create-segments-to-skill-standards-modal/add-create-segments-to-skill-standards-modal.component';
import { AddTagsToSkillStandardsModalComponent } from '@app/skill-standards/components/modals/add-tags-to-skill-standards-modal/add-tags-to-skill-standards-modal.component';
import { SkillStandardsService } from '@app/skill-standards/services/skill-standards.service';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { TagsSearchItem } from '@app/tags/tags';
import { UserProfileSummary } from '@app/user/user-api.model';

@Injectable({
  providedIn: 'root',
})
export class SkillStandardsModalService {
  private readonly i18n = this.translate.instant([
    'Core_ActionError',
    'SkillStd_UnpublishSegment',
    'SkillStd_UnpublishSegmentMsg',
    'SkillStd_UnpublishSegmentMsgConfirm',
  ]);

  constructor(
    private modalService: ModalService,
    private translate: TranslateService,
    private skillStandardsService: SkillStandardsService
  ) {}

  /**
   * Opens modal to add one or more tags to a skill standard
   */
  public openAddTagsToSkillStandardsModal(): Observable<TagsSearchItem[]> {
    return this.modalService.show(AddTagsToSkillStandardsModalComponent, {});
  }

  /**
   * Opens modal to add one or more segments to a skill standard
   */
  public openAddCreateSegmentsToSkillStandardsModal(
    existingSegments = []
  ): Observable<void> {
    return this.modalService.show(
      AddCreateSegmentsToSkillStandardsModalComponent,
      { inputs: { existingSegments: existingSegments } }
    );
  }

  public openPublishSegmentModal(
    orgId,
    skillStandardsId,
    segmentId,
    segmentName
  ) {
    const inputs = {
      bodyText: `Publishing this segment will display all of its associated skills to all users within the segment.<br/><br/>Are you sure you want to publish this segment?`,
      bodyClasses: 'center-text',
      canCancel: true,
      headerText: `Publish to ${segmentName}`,
      submitButtonText: `Publish`,
    };

    return this.modalService
      .show<void>(SimpleModalComponent, {
        inputs,
      })
      .pipe(
        mergeMap(() => {
          const params = {
            organizationId: orgId,
            skillStandardId: skillStandardsId,
            segmentId: segmentId,
            statusId: 1,
          };
          return this.skillStandardsService.updateSegmentSkillStandardStatus(
            params
          );
        })
      );
  }

  public openUnpublishSegmentModal(
    orgId: number,
    skillStandardsId: number,
    segmentId: number,
    segmentName: string
  ) {
    const inputs = {
      bodyText: `${this.i18n.SkillStd_UnpublishSegmentMsg}<br/><br/>${this.i18n.SkillStd_UnpublishSegmentMsgConfirm}`,
      bodyClasses: 'center-text',
      canCancel: true,
      headerText: this.translate.instant('SkillStd_UnpublishSegmentName', {
        segmentName: segmentName,
      }),
      submitButtonText: this.i18n.SkillStd_UnpublishSegment,
    };

    return this.modalService
      .show<void>(SimpleModalComponent, {
        inputs,
      })
      .pipe(
        mergeMap(() => {
          const params = {
            organizationId: orgId,
            skillStandardId: skillStandardsId,
            segmentId: segmentId,
            statusId: 0,
          };
          return this.skillStandardsService.updateSegmentSkillStandardStatus(
            params
          );
        })
      );
  }

  public openViewAllTargetsModal(
    tag,
    targets: any,
    managerView: boolean = false,
    orgId: number = undefined,
    user: UserProfileSummary = undefined
  ) {
    return this.modalService.show<void>(SkillTargetsModalComponent, {
      inputs: {
        targetList: targets,
        tag: tag,
        managerView: managerView,
        orgId: orgId,
        user: user,
      },
      errorOnDismiss: true,
    });
  }
}
